// Display helpers for a Patreon release the REAPER exporter has staged.
// Kept free of React so the button rules can be tested on their own.

export interface StagedPost {
  part: 'audio' | 'video';
  title: string;
  file: string;
  bytes: number | null;
  tiers: string[];
  tags: string[];
  collection: string | null;
  podcast: string | null;
  preview: { start_s: number; duration_s: number } | null;
}

export interface ReleasePart {
  status: 'running' | 'done' | 'error';
  job_status?: string;
  progress_pct?: number;
  patreon_url?: string | null;
  canonical_url?: string | null;
  error?: string | null;
}

export interface PatreonReleasePackage {
  package_id: string;
  site: string;
  release_key: string;
  status: 'staged' | 'releasing' | 'done' | 'error' | 'discarded';
  error: string | null;
  description: string;
  summary: {
    posts: StagedPost[];
    teaser: { title: string; release_date: string | null; file: string; bytes: number | null } | null;
  };
  parts: Record<string, ReleasePart>;
  staged_count: number;
  created_at: string | null;
  updated_at: string | null;
  released_at: string | null;
}

// Short-lived links to play each staged file before Release.
export interface StagedMediaLinks {
  audio: string | null;
  video: string | null;
  teaser: string | null;
  expires_in: number;
}

// The window "Play preview" plays: the post's preview, or none for the teaser.
export const previewEnd = (pv: StagedPost['preview']): number | null =>
  pv ? pv.start_s + pv.duration_s : null;

// Release and Discard exist only while nothing has happened yet. Once a
// release has begun the only action left is retrying what failed.
export const canRelease = (p: PatreonReleasePackage): boolean => p.status === 'staged';
export const canDiscard = (p: PatreonReleasePackage): boolean => p.status === 'staged';
export const canRetry = (p: PatreonReleasePackage): boolean => p.status === 'error';

export const fmtBytes = (n: number | null | undefined): string => {
  if (n == null) return '';
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(0)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

export const fmtClock = (seconds: number): string => {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};

export const fmtPreview = (pv: StagedPost['preview']): string =>
  pv ? `${fmtClock(pv.start_s)} to ${fmtClock(pv.start_s + pv.duration_s)} (${pv.duration_s}s)` : 'none';

// What the Release button will do, in one sentence the person can check.
export const releaseSentence = (p: PatreonReleasePackage): string => {
  const kinds = p.summary.posts.map((x) => x.part);
  const posts = kinds.length === 2 ? 'the audio post and the video post'
    : kinds.length === 1 ? `the ${kinds[0]} post` : 'nothing';
  const teaser = p.summary.teaser ? ', and starts the bonus teaser for the podcast feed' : '';
  return `Publishes ${posts} to Patreon${teaser}.`;
};
